import type { Props } from 'davaux/jsx-runtime'
import type { StyleObject, StyleProps, UiColor, UiRadius, UiSize } from './types.js'
import { cx, mergeStyle, pickStyleProps, radius } from './types.js'

const ROOT = 'dv-checkbox'
const INPUT_CLS = 'dv-checkbox-input'
const LABEL_CLS = 'dv-checkbox-label'
const DESC_CLS = 'dv-checkbox-description'
const ERR_CLS = 'dv-checkbox-error'

/** Controls which side of the checkbox the label is rendered on. */
export type CheckboxLabelPosition = 'right' | 'left'

/** Props for the Checkbox component. */
export interface CheckboxProps extends StyleProps {
  /** Associates the label with the checkbox via the HTML `for`/`id` attribute pair. */
  id?: string
  /** Text or elements rendered as the clickable label beside the checkbox. */
  label?: Props['children']
  /** Helper text rendered below the label. */
  description?: Props['children']
  /** Renders an error message below the checkbox and applies error styles; pass `true` to show styles without a message. */
  error?: Props['children'] | boolean
  /** Renders the checkbox in the checked state (SSR snapshot). */
  checked?: boolean
  /** Renders the checkbox checked on first render without controlling subsequent state. */
  defaultChecked?: boolean
  /** Renders the checkbox in an indeterminate (mixed) state via `data-indeterminate`. */
  indeterminate?: boolean
  /** Disables the checkbox and prevents interaction. */
  disabled?: boolean
  /** Marks the field as required in the browser. */
  required?: boolean
  /** Controls the size of the checkbox icon and hit area via design-token size steps. */
  size?: UiSize
  /** Sets the border-radius of the checkbox box via a design-token radius step or a CSS value. */
  radius?: UiRadius
  /** Sets the filled background color of the checked checkbox. */
  color?: UiColor
  /** Sets the color of the checkmark icon inside the checkbox. */
  iconColor?: UiColor
  /** Places the label to the right or left of the checkbox input. */
  labelPosition?: CheckboxLabelPosition
  /** Name submitted with the form, used to group checkboxes. */
  name?: string
  /** Value submitted with the form when the checkbox is checked. */
  value?: string
  [key: string]: unknown
}

/** Renders a styled `<input type="checkbox">` with optional label, description, and error message. */
export function Checkbox({
  id: idProp,
  label,
  description,
  error,
  checked,
  defaultChecked,
  indeterminate,
  disabled,
  required,
  size,
  radius: r,
  color,
  iconColor,
  labelPosition = 'right',
  name,
  value,
  class: className,
  style,
  ...rest
}: CheckboxProps): Promise<string> {
  const id = idProp ?? `dv-checkbox-${Math.random().toString(36).slice(2, 9)}`

  const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)
  const cssVars: StyleObject = {}
  if (size !== undefined) cssVars['--dv-checkbox-size'] = `var(--dv-checkbox-size-${size})`
  if (r !== undefined) cssVars['--dv-checkbox-radius'] = radius(r) ?? ''
  if (color !== undefined) cssVars['--dv-checkbox-color'] = `var(--dv-color-${color}-filled)`
  if (iconColor !== undefined) cssVars['--dv-checkbox-icon-color'] = iconColor

  const hasError = !!error

  return (
    <div
      class={cx(ROOT, className)}
      style={mergeStyle({ ...cssVars, ...styleObj }, style)}
      data-checked={checked || undefined}
      data-indeterminate={indeterminate || undefined}
      data-disabled={disabled || undefined}
      data-label-position={label ? labelPosition : undefined}
    >
      <input
        class={INPUT_CLS}
        type='checkbox'
        id={id}
        name={name}
        value={value}
        checked={checked || undefined}
        defaultChecked={defaultChecked || undefined}
        disabled={disabled || undefined}
        required={required || undefined}
        data-indeterminate={indeterminate || undefined}
        data-error={hasError || undefined}
        {...ownRest}
      />
      {label !== undefined && (
        <label class={LABEL_CLS} for={id}>
          {label}
        </label>
      )}
      {description !== undefined && <p class={DESC_CLS}>{description}</p>}
      {hasError && error !== true && <p class={ERR_CLS}>{error as Props['children']}</p>}
    </div>
  )
}
